import { getEasySellStore, refreshEasySellForm } from '@theme/easysell-store';

/**
 * Keeps EasySell's order form from ordering products that are out of stock.
 *
 * EasySell places its orders itself, so Shopify checkout never gets to refuse a sold-out line, and the cart takes one
 * in: the app's product-page button writes the variant with /cart/update.js. Before the form's order button goes
 * through, this asks sections/section-rendering-cart-stock.liquid whether every variant in the cart can still be
 * ordered in the quantity the app holds. When one can't, the section's message goes into the app's own error list
 * under the button and the press stops there. The same check runs when the form opens and whenever the app reloads its
 * cart, so the shopper sees the problem before filling in their details. When stock can't be read, the order goes
 * through as it did before this check existed.
 */

const SECTION_ID = 'section-rendering-cart-stock';
const ORDER_BUTTON_SELECTOR = '#easysell .es-button';

/** Cart reloads come in bursts (the app, the cart editor, Bundlex), so check once they settle. */
const CHECK_DELAY = 200;

/** A check this recent still answers a reload that left the list as it was. Order presses always check again. */
const CHECK_MAX_AGE = 5000;

/**
 * @typedef {object} VariantStock
 * @property {number} id - The variant id.
 * @property {number} quantity - The variant's total quantity across cart lines.
 * @property {boolean} ok - Whether that quantity can be ordered.
 * @property {string} message - Why not, in the shopper's language.
 */

/**
 * @typedef {object} Verdict
 * @property {string} list - The app's list the verdict is about, as sorted variant:quantity pairs.
 * @property {'ok' | 'blocked' | 'unchecked'} result - `unchecked` when stock couldn't be read.
 * @property {number} checkedAt
 */

/** @type {Verdict | null} */
let verdict = null;

/** Checks run one after another, so a cart reload can't race an order press. */
let queue = Promise.resolve();

/** @type {EasySellError[] | null} The messages this module showed; the app's own errors are left alone. */
let shownErrors = null;

/** @type {HTMLButtonElement | null} The order button whose press this module checked and is letting through. */
let releasedButton = null;

/** Set while a press waits for its check; further presses are dropped. */
let holding = false;

/** @type {EasySellStore | null} */
let watchedStore = null;

/** @type {ReturnType<typeof setTimeout> | undefined} */
let checkTimer;

/** @type {Window['es_hook_before_submit']} */
let previousSubmitHook;

/**
 * The app's list as variant id → total quantity, which is what its order sends.
 * @param {EasySellStore} store
 * @returns {Map<number, number>}
 */
function getQuantities(store) {
  /** @type {Map<number, number>} */
  const quantities = new Map();

  for (const item of store.state.cart.cart.items) {
    const id = Number(item.variant_id);
    quantities.set(id, (quantities.get(id) ?? 0) + Number(item.quantity));
  }

  return quantities;
}

/** @param {Map<number, number>} quantities */
function describe(quantities) {
  return Array.from(quantities, ([id, quantity]) => `${id}:${quantity}`)
    .sort()
    .join(',');
}

/**
 * @param {Map<number, number>} quantities
 * @param {VariantStock[]} variants
 */
function matchesCart(quantities, variants) {
  return (
    variants.length === quantities.size &&
    variants.every((variant) => quantities.get(Number(variant.id)) === Number(variant.quantity))
  );
}

/** @returns {Promise<{ retryMessage: string, variants: VariantStock[] }>} */
async function fetchStock() {
  const url = new URL(Theme.routes.cart_url, window.location.origin);
  url.searchParams.set('section_id', SECTION_ID);

  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) throw new Error(`Stock request failed: ${response.status}`);

  // The section's messages are HTML-escaped translations; parsing the markup turns them back into plain text.
  const html = new DOMParser().parseFromString(await response.text(), 'text/html');
  const stock = html.querySelector('[data-cart-stock]');
  if (!stock) throw new Error('The stock response has no cart stock');

  return {
    retryMessage: stock.getAttribute('data-retry-message') ?? '',
    variants: Array.from(stock.querySelectorAll('[data-variant-id]'), (entry) => ({
      id: Number(entry.getAttribute('data-variant-id')),
      quantity: Number(entry.getAttribute('data-quantity')),
      ok: entry.getAttribute('data-ok') === 'true',
      message: entry.textContent?.trim() ?? '',
    })),
  };
}

/**
 * Puts the messages in the app's error list under the order button, or clears the ones this module put there.
 * @param {EasySellStore} store
 * @param {string[]} messages
 */
function showErrors(store, messages) {
  if (messages.length === 0) {
    if (shownErrors && store.state.form?.errors === shownErrors) store.commit('form/SET_ERRORS', []);
    shownErrors = null;
    return;
  }

  shownErrors = messages.map((message) => ({ message }));
  store.commit('form/SET_ERRORS', shownErrors);
}

/**
 * @param {EasySellStore} store
 * @returns {Promise<Verdict>}
 */
async function check(store) {
  let quantities = getQuantities(store);
  let stock;

  try {
    stock = await fetchStock();

    if (!matchesCart(quantities, stock.variants)) {
      // The cart changed after the app loaded it (another tab, a late Bundlex line). The app orders its own copy,
      // so bring that up to date before judging it.
      await refreshEasySellForm(store);
      quantities = getQuantities(store);
      stock = await fetchStock();
    }
  } catch (error) {
    // A network or theme fault must not stop every order.
    console.warn('[easysell] Could not check stock before ordering:', error);
    return { list: describe(quantities), result: 'unchecked', checkedAt: Date.now() };
  }

  const { retryMessage } = stock;
  const problems = matchesCart(quantities, stock.variants)
    ? stock.variants.filter((variant) => !variant.ok).map((variant) => variant.message || retryMessage)
    : [retryMessage];

  showErrors(store, problems);
  return { list: describe(quantities), result: problems.length > 0 ? 'blocked' : 'ok', checkedAt: Date.now() };
}

/**
 * Checks the app's current list once the checks before it have finished.
 * @param {EasySellStore} store
 * @param {{ fresh?: boolean }} [options] - `fresh` checks again even when the same list was checked moments ago.
 * @returns {Promise<Verdict>}
 */
function verify(store, { fresh = false } = {}) {
  const run = queue.then(async () => {
    const list = describe(getQuantities(store));
    if (!fresh && verdict?.list === list && Date.now() - verdict.checkedAt < CHECK_MAX_AGE) return verdict;

    verdict = await check(store);
    return verdict;
  });

  queue = run.catch(() => {});
  return run;
}

/** The form is on screen, open as a popup or embedded in the page. */
function isFormVisible() {
  const form = document.getElementById('easysell');
  return form instanceof HTMLElement && form.getClientRects().length > 0;
}

/** @param {EasySellStore} store */
function scheduleCheck(store) {
  clearTimeout(checkTimer);
  checkTimer = setTimeout(() => {
    if (isFormVisible()) verify(store).catch(() => {});
  }, CHECK_DELAY);
}

/**
 * EasySell runs this synchronously before it submits and cancels when it throws `block`. Order presses are checked
 * in handleClick first; this stops anything else that submits the form (the app also saves unfinished orders in the
 * background) while the list is unchecked or blocked.
 * @param {Parameters<NonNullable<Window['es_hook_before_submit']>>[0]} data
 */
function beforeSubmit(data) {
  const store = getEasySellStore();

  if (store) {
    if (verdict?.list !== describe(getQuantities(store))) {
      verify(store).catch(() => {});
      throw new Error('block');
    }

    if (verdict.result === 'blocked') throw new Error('block');
  }

  previousSubmitHook?.(data);
}

/** Wraps the app's hook, including the one assets/easysell-checkout.js sets, whichever module ran first. */
function installSubmitHook() {
  if (window.es_hook_before_submit === beforeSubmit) return;

  previousSubmitHook = window.es_hook_before_submit;
  window.es_hook_before_submit = beforeSubmit;
}

/**
 * Checks again whenever the app reloads its cart, so a sold-out line is flagged while the shopper fills in the form.
 * @param {EasySellStore} store
 */
function watch(store) {
  installSubmitHook();
  if (watchedStore === store) return;

  watchedStore = store;
  store.subscribe((mutation) => {
    if (mutation.type === 'cart/SET_CART') scheduleCheck(store);
  });
}

/** Moves focus to the app's error list so the reason is read out. */
function focusErrors() {
  const list = document.getElementById('es-errors');
  if (!(list instanceof HTMLElement) || !list.textContent?.trim()) return;

  list.tabIndex = -1;
  list.focus();
}

/** @param {MouseEvent} event */
async function handleClick(event) {
  const button = event.target instanceof Element ? event.target.closest(ORDER_BUTTON_SELECTOR) : null;
  if (!(button instanceof HTMLButtonElement)) return;

  if (button === releasedButton) {
    releasedButton = null;
    return;
  }

  const store = getEasySellStore();
  if (!store) return;
  watch(store);

  // The app submits inside its own click handler, so the press waits here until the list is known to be in stock.
  event.preventDefault();
  event.stopImmediatePropagation();
  if (holding) return;

  holding = true;
  button.setAttribute('aria-busy', 'true');

  /** @type {Verdict['result']} */
  let result = 'unchecked';
  try {
    ({ result } = await verify(store, { fresh: true }));
  } catch (error) {
    console.warn('[easysell] Could not check stock before ordering:', error);
  } finally {
    holding = false;
    button.removeAttribute('aria-busy');
  }

  if (result === 'blocked') {
    // Let the app draw its error list first.
    requestAnimationFrame(focusErrors);
    return;
  }

  if (!button.isConnected) return;
  releasedButton = button;
  button.click();
  releasedButton = null;
}

/**
 * Runs `callback` from one of the app's lifecycle hooks, keeping a hook defined before this module.
 * @param {'es_after_init_completed' | 'es_hook_open_popup'} name
 * @param {(store: EasySellStore) => void} callback
 */
function onAppHook(name, callback) {
  const previous = window[name];

  window[name] = () => {
    previous?.();
    const store = getEasySellStore();
    if (store) callback(store);
  };
}

document.addEventListener('click', handleClick, { capture: true });
onAppHook('es_after_init_completed', watch);
onAppHook('es_hook_open_popup', (store) => {
  watch(store);
  scheduleCheck(store);
});

const initialStore = getEasySellStore();
if (initialStore) watch(initialStore);
